import { supabase } from './client'
import { matchesServiceArea } from '@lib/constants/nigerianServiceAreas'

export interface Provider {
  id: string
  auth_id: string
  name: string
  specialty: string
  phone_number: string
  email?: string
  address?: string
  profile_image_url: string | null
  is_verified: boolean
  account_status: string
  is_active: boolean
  gender?: string
  // Extended profile fields
  qualification?: string | null
  experience?: number | null
  years_of_experience?: number | null
  about?: string | null
  bio?: string | null
  languages?: string[] | null
  specializations?: string[] | null
  // Relations
  service_area?: { state: string; country: string } | null
  provider_service_areas?: Array<{ state: string; country: string; travel_radius?: number; is_active: boolean }> | null
  activeServiceRate?: { id: string; is_active: boolean; base_rate: number | null } | null
  created_at: string
}

export interface ServiceCategory {
  id: string
  type?: string
  title?: string
  name?: string
  description?: string
  color?: string
}

export interface ProviderSearchParams {
  searchQuery?: string
  serviceType?: string
  state?: string
}

export interface RatingStats {
  avgRating: number
  totalReviews: number
}

const normalize = (value?: string | null) => (value || '').toLowerCase().trim()

export const providerService = {
  async getAllProviders(): Promise<Provider[]> {
    try {
      const { data, error } = await supabase
        .from('providers')
        .select('*, provider_service_areas(*)')
        .eq('is_verified', true)
        .eq('account_status', 'approved')
        .order('name')
      if (error) throw error
      return data || []
    } catch {
      return []
    }
  },

  async getProviderById(id: string): Promise<Provider | null> {
    if (!id) return null
    try {
      const { data, error } = await supabase
        .from('providers')
        .select('*')
        .eq('id', id)
        .single()
      if (error) throw error
      return data
    } catch {
      return null
    }
  },

  async getProvidersByIds(ids: string[]): Promise<Record<string, Provider>> {
    const uniqueIds = Array.from(new Set(ids.filter(Boolean))).sort()
    if (uniqueIds.length === 0) return {}

    try {
      const { data, error } = await supabase
        .from('providers')
        .select('*')
        .in('id', uniqueIds)

      if (error) throw error

      return ((data || []) as Provider[]).reduce<Record<string, Provider>>((acc, provider) => {
        acc[provider.id] = provider
        return acc
      }, {})
    } catch {
      return {}
    }
  },

  async searchProviders(params: ProviderSearchParams): Promise<Provider[]> {
    try {
      const { data, error } = await supabase.rpc('search_providers', {
        search_query: params.searchQuery || undefined,
        service_type_param: params.serviceType || undefined,
        state_param: params.state || undefined,
        min_rating_param: 0,
      })
      if (error) throw error
      return data || []
    } catch {
      return []
    }
  },

  async getServiceCategories(): Promise<ServiceCategory[]> {
    try {
      const { data, error } = await supabase.from('service_categories').select('*')
      if (error) throw error
      return data || []
    } catch {
      return []
    }
  },

  async getProvidersByService(serviceName: string, options?: { state?: string }): Promise<Provider[]> {
    try {
      const isChatConsultation = normalize(serviceName) === 'chat consultation'

      // 1. Find the matching service category
      const { data: categories, error: catError } = await supabase
        .from('service_categories')
        .select('*')
      if (catError) throw catError

      const matchedCategory = (categories || []).find((cat: any) => {
        const label = normalize(cat.type) || normalize(cat.title) || normalize(cat.name)
        return label === normalize(serviceName)
      })

      if (!matchedCategory?.id) return []

      // 2. Build the select — Chat Consultation doesn't require active service areas
      const selectQuery = isChatConsultation
        ? `
            *,
            provider_service_areas (state, is_active, travel_radius),
            provider_availability (
              day_of_week, is_available,
              provider_time_slots (start_time, end_time)
            ),
            service_rates!inner (id, is_active, base_rate)
          `
        : `
            *,
            provider_service_areas!inner (state, is_active, travel_radius),
            provider_availability!inner (
              day_of_week, is_available,
              provider_time_slots (start_time, end_time)
            ),
            service_rates!inner (id, is_active, base_rate)
          `

      // 3. Query: only providers with an *active* service rate for this category
      let query = supabase
        .from('providers')
        .select(selectQuery)
        .eq('is_verified', true)
        .eq('account_status', 'approved')
        .eq('service_rates.service_category_id', matchedCategory.id)
        .eq('service_rates.is_active', true)

      if (!isChatConsultation) {
        query = query.eq('provider_service_areas.is_active', true)
      }

      const { data, error } = await query
      if (error) throw error

      // 4. Attach the active service rate for easy access downstream
      let providers = ((data || []) as Provider[]).map((p: any) => ({
        ...p,
        activeServiceRate: p.service_rates?.[0] ?? null,
      }))

      if (options?.state && !isChatConsultation) {
        providers = providers.filter((provider) =>
          provider.provider_service_areas?.some((area: { state: string; is_active: boolean }) =>
            area.is_active && matchesServiceArea(options.state!, area.state)
          )
        )
      }

      return providers
    } catch {
      return []
    }
  },

  async getProviderRatingStats(id: string): Promise<RatingStats> {
    try {
      const { data, error } = await supabase.rpc('get_provider_rating_stats', {
        provider_id_param: id,
      })
      if (error) throw error
      if (data && typeof data === 'object') {
        const stats = data as Partial<{ totalReviews: number; avgRating: number }>
        return {
          avgRating: stats.avgRating ?? 0,
          totalReviews: stats.totalReviews ?? 0,
        }
      }
      return { avgRating: 0, totalReviews: 0 }
    } catch {
      return { avgRating: 0, totalReviews: 0 }
    }
  },

  /** Service rates for a provider — merged with service_categories */
  async getProviderServiceRates(providerId: string): Promise<ServiceRate[]> {
    try {
      const { data: categories, error: catError } = await supabase
        .from('service_categories')
        .select('*')
      if (catError) throw catError

      const { data: rates, error: ratesError } = await supabase
        .from('service_rates')
        .select('*')
        .eq('provider_id', providerId)
      if (ratesError) throw ratesError

      return (categories || []).map((category: any) => {
        const providerRate = (rates || []).find(
          (r: any) => r.service_category_id === category.id
        )
        const serviceName =
          category.title || category.name || category.type || 'Service'
        return {
          id: providerRate?.id || `new-${category.id}`,
          service_category_id: category.id,
          service_name: serviceName as string,
          description: category.description as string | undefined,
          base_rate: providerRate?.base_rate ?? category.base_price ?? null,
          is_active: providerRate?.is_active ?? false,
          has_rate: !!providerRate,
        }
      })
    } catch {
      return []
    }
  },

  /** Availability schedule with nested time slots */
  async getProviderAvailability(providerId: string): Promise<ProviderAvailabilitySlot[]> {
    try {
      const { data, error } = await supabase
        .from('provider_availability')
        .select('*, provider_time_slots(*)')
        .eq('provider_id', providerId)
        .order('day_of_week')
      if (error) throw error
      return (data || []) as ProviderAvailabilitySlot[]
    } catch {
      return []
    }
  },

  /** Reviews for a provider */
  async getProviderReviews(providerId: string): Promise<ProviderReview[]> {
    try {
      const { data, error } = await supabase
        .from('reviews')
        .select('*, patients(name, profile_image_url)')
        .eq('provider_id', providerId)
        .order('created_at', { ascending: false })
        .limit(50)
      if (error) throw error
      return (data || []).map((r: any) => ({
        id: r.id,
        patientName: (r.patients as any)?.name || 'Anonymous',
        rating: r.rating ?? 0,
        comment: r.comment ?? '',
        date: r.created_at || '',
        service: r.service ?? r.service_type ?? '',
        verified: r.verified ?? r.is_verified ?? false,
      }))
    } catch {
      return []
    }
  },
}

export interface ServiceRate {
  id: string
  service_category_id: string
  service_name: string
  description?: string
  base_rate: number | null
  is_active: boolean
  has_rate: boolean
}

export interface ProviderAvailabilitySlot {
  id: string
  provider_id: string
  day_of_week: string
  is_available: boolean
  provider_time_slots: Array<{ start_time: string; end_time: string }>
}

export interface ProviderReview {
  id: string
  patientName: string
  rating: number
  comment: string
  date: string
  service: string
  verified: boolean
}
